import { useMemo } from "react";

/** Escapes regex metacharacters so a query like "+92" or "a.b" cannot throw or over-match. */
function escapeRegex(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, (match) => `\\${match}`);
}

export interface HighlightProps {
  /** The full text to render. */
  text: string | null | undefined;
  /** The active search query. Empty or whitespace renders the text untouched. */
  query: string;
  className?: string;
}

/**
 * Marks the part of `text` that matched the search, so a filtered list shows WHY each row
 * survived rather than just silently shrinking.
 *
 * Uses <mark> rather than a styled <span>: it carries the meaning natively, and the default
 * yellow browser styling is overridden with the accent-soft token so it belongs to the theme in
 * both light and dark.
 */
export function Highlight({ text, query, className }: HighlightProps) {
  const value = text ?? "";
  const needle = query.trim();

  const parts = useMemo(() => {
    if (!needle) return null;
    // Split on the query while KEEPING it, so matched segments land on the odd indices.
    return value.split(new RegExp(`(${escapeRegex(needle)})`, "gi"));
  }, [value, needle]);

  if (!parts || parts.length === 1) return <span className={className}>{value}</span>;

  return (
    <span className={className}>
      {parts.map((part, i) =>
        part.toLowerCase() === needle.toLowerCase() ? (
          <mark key={i} className="rounded-[3px] bg-accent-soft px-0.5 font-medium text-accent">
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </span>
  );
}
